import React, { useCallback, useRef } from "react";
import _ from "lodash";

import { colors } from "theme/palette";

import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import EditSectionForm from "entities/MemoTemplate/components/EditSectionForm";
import { EMemoSectionType } from "entities/MemoTemplate/constants";
import { IMemoTemplateSection } from "entities/MemoTemplate/sdk";

const EditSectionDialog = ({
  section,
  open,
  onClose,
  onSectionEditted,
}: {
  section: IMemoTemplateSection;
  open: boolean;
  onClose: () => void;
  onSectionEditted: () => void;
}) => {
  const formikRef = useRef<any>();

  const handleSubmit = () => {
    if (formikRef.current) {
      formikRef.current.handleSubmit();
    }
  };

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const showConfirmDialog = useCallback(
    () =>
      showConfirmationDialog({
        onConfirm: () => handleSubmit(),
        message:
          "Updating this section table will remove all table modifications in every deal.",
        confirmButtonText: "Update",
      }),
    [showConfirmationDialog]
  );

  const handleUpdateClick = () => {
    if (
      section.section_type === EMemoSectionType.CHECKLIST_TABLE &&
      formikRef.current &&
      (!_.isEqual(
        formikRef.current.initialValues.checklist_table,
        formikRef.current.values.checklist_table.map((row: string[]) =>
          row.map((colVal: string) => colVal.trim())
        )
      ) ||
        !_.isEqual(
          formikRef.current.initialValues.checklist_table_editable_cells,
          formikRef.current.values.checklist_table_editable_cells
        ))
    ) {
      showConfirmDialog();
      return;
    }
    handleSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ borderBottom: `1px solid ${colors.blue20}` }}>
        <Text variant="h3">Edit Section</Text>
      </DialogTitle>
      <DialogContent>
        <EditSectionForm
          section={section}
          onCancel={onClose}
          onSectionEditted={onSectionEditted}
          formikRef={formikRef}
        />
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${colors.blue20}` }}>
        <Stack direction="row" justifyContent="end" spacing={2}>
          <Button variant="text" size="large" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" size="large" onClick={handleUpdateClick}>
            Update
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default EditSectionDialog;
